// An unlock that ran out while the account was open.
//
// accountSession holds the unlock tokens as opaque strings and never reads
// their expiry, so when one lapses the tab still believes the account is open:
// every panel on it starts answering 401 ACCOUNT_LOCKED and nothing offers a
// way back in short of closing the browser tab. That was survivable at 12
// hours. The break-glass override and the view-only open both last ONE hour,
// so for a view-only staffer it would happen every working session.
//
// The server's own answer is the signal. When an account-scoped call comes
// back ACCOUNT_LOCKED for an account this tab thinks is unlocked, the stale
// token is dropped and the app is told, so it can ask again.

import { isUnlocked, lockAccount } from './accountSession.js';

const ACCOUNT_URL = /^\/api\/(?:accounts|customer)\/([A-Za-z0-9]{15,18})(?:\/|$)/;

/** The account an /api/accounts/<id>/… or /api/customer/<id> URL addresses, or null. */
export function accountIdOfApiUrl(url) {
  try {
    const { pathname } = new URL(String(url), 'https://work.squish.co');
    return ACCOUNT_URL.exec(pathname)?.[1] || null;
  } catch {
    return null;
  }
}

/**
 * Look at one response. If it says the account this tab holds an unlock for is
 * locked, forget the unlock and call onLocked(accountId) — once: after the
 * first, isUnlocked is false and the rest of that page's failing calls are
 * ignored here.
 */
export async function noteAccountLock(response, url, { onLocked, method = 'GET' } = {}) {
  // A WRITE answering ACCOUNT_LOCKED does not mean the unlock expired: an
  // account opened view-only is unlocked for reads and locked for writes, by
  // design, and dropping its token would close an account that is working.
  // An unlock that really has run out shows on the page's reads too.
  if (!['GET', 'HEAD'].includes(String(method || 'GET').toUpperCase())) return false;
  if (!response || (response.status !== 401 && response.status !== 403)) return false;
  const id = accountIdOfApiUrl(url);
  if (!id || !isUnlocked(id)) return false;
  let body;
  try { body = await response.clone().json(); } catch { return false; }
  if (body?.errorCode !== 'ACCOUNT_LOCKED') return false;
  if (!isUnlocked(id)) return false;   // a sibling call got here first
  lockAccount(id);
  if (typeof onLocked === 'function') onLocked(id);
  return true;
}
